// LiveQuiz — Orquesta el modo "pregunta interactiva en vivo" durante la
// sala de clase. Reacciona a mensajes WS quiz_launched / quiz_tally /
// quiz_closed que llegan por el canal del whiteboard. Renderiza:
//   - Para el docente: panel "Lanzar" + tally en vivo
//   - Para el estudiante: modal de respuesta cuando hay pregunta activa
import { useEffect, useMemo, useState, useRef, Component } from "react";
import { CheckCircle2, X, Play, Square, Sparkles, Library, Wand2, Clock, BarChart3 } from "lucide-react";
import { api, getAccessToken } from "@/lib/api";
import { toast } from "sonner";
import { RenderTextWithMath } from "@/components/MathFormulaEditor";
import QuestionBankModal from "@/components/QuestionBankModal";
import QuestionResponder from "@/components/QuestionResponder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

// Error boundary local: si el subárbol del quiz falla por cualquier motivo,
// NO debe tumbar toda la sala de clase (antes el estudiante veía la pantalla
// "¡Ups!" al reingresar con una pregunta activa). El quiz simplemente se oculta.
class QuizErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("LiveQuiz crash contenido por boundary:", error, info);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default function LiveQuiz(props) {
  return (
    <QuizErrorBoundary>
      <LiveQuizInner {...props} />
    </QuizErrorBoundary>
  );
}

function LiveQuizInner({ classId, canModerate }) {
  // Estado de la pregunta activa (visto desde cualquier rol)
  const [active, setActive] = useState(null);
  const [tally, setTally] = useState({ total: 0, correct: 0, distribution: {}, respondents: [] });
  const [myAnswered, setMyAnswered] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [showLaunch, setShowLaunch] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const wsRef = useRef(null);
  const lastActiveIdRef = useRef(null);
  const activeRef = useRef(null);
  useEffect(() => { activeRef.current = active; }, [active]);

  // Aplica una pregunta activa de forma idempotente. Si es NUEVA (id distinto),
  // reinicia el estado (minimizado, respondida, tally). Se usa desde el fetch
  // inicial, el WS y el polling de respaldo, para un comportamiento consistente.
  const applyActive = (a, myResp, tallyData) => {
    if (!a) return;
    if (lastActiveIdRef.current !== a.id) {
      lastActiveIdRef.current = a.id;
      setMinimized(false);
      setMyAnswered(!!myResp);
      setTally(tallyData || { total: 0, correct: 0, distribution: {}, respondents: [] });
    } else if (tallyData) {
      setTally(tallyData);
    }
    setActive(a);
  };

  // Cargar estado al montar — por si entras tarde a la sala
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/live-class/${classId}/quiz/active`);
        if (data.active) {
          applyActive(data.active, data.my_response, data.tally);
        }
      } catch { /* sin pregunta activa */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // Polling de respaldo: si el WebSocket falla o llega tarde (p. ej. en
  // producción tras un proxy/ingress), garantiza que el estudiante VEA la
  // pregunta y que el docente reciba el tally en (casi) tiempo real. El docente
  // sondea más rápido (3s) para ver las respuestas en vivo aunque no haya WS.
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/live-class/${classId}/quiz/active`);
        if (data.active && data.active.status === "active") {
          applyActive(data.active, data.my_response, data.tally);
        } else if (!data.active && activeRef.current && activeRef.current.status === "active") {
          // La pregunta se cerró y el WS no entregó el evento (bloqueado en
          // producción). Transicionamos a "cerrada" para que el estudiante vea
          // resultados y el botón "Continuar con la clase".
          const closingId = activeRef.current.id;
          try {
            const r = await api.get(`/live-class/${classId}/quiz/${closingId}/results`);
            if (r.data?.tally) setTally(r.data.tally);
          } catch { /* ignore */ }
          setActive((prev) => (prev && prev.status === "active" ? { ...prev, status: "closed", closed_reason: "polled" } : prev));
        }
      } catch { /* ignore */ }
    }, canModerate ? 3000 : 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, canModerate]);

  // WebSocket — escucha los broadcasts de quiz
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    const ws = new WebSocket(`${WS_BASE}/api/ws/whiteboard/${classId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "quiz_launched") {
        applyActive(msg.question, false, null);
        if (!canModerate) toast.info("¡Nueva pregunta en vivo!");
      } else if (msg.type === "quiz_tally") {
        setTally(msg.tally || tally);
      } else if (msg.type === "quiz_closed") {
        setTally(msg.tally || tally);
        // Mantenemos la pregunta visible para ver resultados; añadimos flag closed
        setActive((prev) => prev ? { ...prev, status: "closed", closed_reason: msg.reason } : prev);
      }
    };
    return () => { try { ws.close(); } catch { /* ws ya cerrado */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // ---- Acciones ----
  const launchFromBank = async (pick) => {
    if (!pick || !pick.id) return;
    setLaunching(true);
    try {
      const { data } = await api.post(`/live-class/${classId}/quiz/launch`, {
        source: "bank",
        question_id: pick.id,
        // C59 — enviamos template_code (y bloque) para desambiguar cuando el
        // mismo id de pregunta se repite entre plantillas del banco.
        template_code: pick.template_code || null,
        block_id: pick.block_id || pick._source_block || null,
      });
      if (data?.live_question) applyActive(data.live_question, false, null);
      toast.success("Pregunta lanzada");
      setShowLaunch(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo lanzar la pregunta");
    } finally {
      setLaunching(false);
    }
  };

  const launchInline = async (q, timeLimit) => {
    setLaunching(true);
    try {
      const { data } = await api.post(`/live-class/${classId}/quiz/launch`, {
        source: "inline",
        question_data: q,
        time_limit_seconds: timeLimit || null,
      });
      if (data?.live_question) applyActive(data.live_question, false, null);
      toast.success("Pregunta lanzada");
      setShowLaunch(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo lanzar");
    } finally {
      setLaunching(false);
    }
  };

  const closeNow = async () => {
    if (!active) return;
    try {
      await api.post(`/live-class/${classId}/quiz/${active.id}/close`);
      // Optimista: marca cerrada localmente sin depender del eco del WS.
      setActive((prev) => (prev ? { ...prev, status: "closed", closed_reason: "manual" } : prev));
      toast.success("Pregunta cerrada");
    } catch {
      toast.error("No se pudo cerrar");
    }
  };

  const submitAnswer = async (answer) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/live-class/${classId}/quiz/${active.id}/answer`, { answer });
      setMyAnswered(true);
      setTally(data.tally || tally);
      if (data.is_correct === true) toast.success("¡Respuesta correcta! 🎉");
      else if (data.is_correct === false) toast.warning("Respuesta enviada (no fue la correcta)");
      else toast.success("Respuesta enviada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar");
    }
  };

  // ---- Render ----
  return (
    <div data-testid="live-quiz">
      {/* DOCENTE: Botón lanzar (visible si no hay activa, o si la activa está cerrada) */}
      {canModerate && (!active || active.status === "closed") && (
        <button
          type="button"
          onClick={() => setShowLaunch(true)}
          data-testid="live-quiz-launch-btn"
          className="px-3 py-1.5 rounded-full bg-[#A43790] text-white text-xs font-extrabold inline-flex items-center gap-1.5 shadow-md hover:bg-[#80286D]"
        >
          <Sparkles size={12} /> Lanzar pregunta
        </button>
      )}

      {/* DOCENTE: Tally en vivo + botón cerrar (cuando hay pregunta activa) */}
      {canModerate && active && active.status !== "closed" && (
        <TeacherPanel active={active} tally={tally} onClose={closeNow} />
      )}

      {/* DOCENTE: Resultados finales tras cierre */}
      {canModerate && active && active.status === "closed" && (
        <TeacherResults active={active} tally={tally} onDismiss={() => setActive(null)} />
      )}

      {/* ESTUDIANTE: Modal de respuesta (con opción de minimizar para no quedar
          atrapado y poder salir de la sala) */}
      {!canModerate && active && !minimized && (
        <StudentModal
          active={active}
          tally={tally}
          alreadyAnswered={myAnswered}
          onSubmit={submitAnswer}
          onMinimize={() => setMinimized(true)}
          onDismiss={() => { if (active.status === "closed") { setActive(null); setMinimized(false); } }}
        />
      )}

      {/* ESTUDIANTE: píldora flotante para reabrir la pregunta minimizada */}
      {!canModerate && active && minimized && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          data-testid="student-quiz-reopen"
          className="fixed bottom-4 right-4 z-[120] px-4 py-2.5 rounded-full bg-[#A43790] text-white text-sm font-extrabold inline-flex items-center gap-2 shadow-2xl hover:bg-[#80286D] animate-pulse"
        >
          <Sparkles size={14} /> {active.status === "closed" ? "Ver resultados" : "Ver pregunta en vivo"}
        </button>
      )}

      {/* DOCENTE: Modal de lanzamiento */}
      {showLaunch && (
        <LaunchModal
          classId={classId}
          onClose={() => setShowLaunch(false)}
          onLaunchBank={launchFromBank}
          onLaunchInline={launchInline}
          launching={launching}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------
function TeacherPanel({ active, tally, onClose }) {
  const q = active.question || {};
  return (
    <div className="fixed bottom-4 right-4 z-[70] w-[360px] max-w-[92vw] jasq-card p-3 border-2 border-[#A43790] bg-white shadow-2xl space-y-2" data-testid="teacher-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">
            Pregunta en vivo · <span className="text-[#1F2547]">{q.type}</span>
          </p>
          <p className="text-sm font-bold text-[#1F2547] line-clamp-2"><RenderTextWithMath text={q.text || ""} /></p>
        </div>
      </div>
      <TallyView question={q} tally={tally} />
      <button onClick={onClose} className="w-full mt-1 px-3 py-2 rounded-xl bg-[#1F2547] text-white text-sm font-extrabold inline-flex items-center justify-center gap-1.5 hover:bg-[#161A33] transition-colors" data-testid="teacher-close-quiz">
        <Square size={12} /> Cerrar pregunta
      </button>
    </div>
  );
}

function TeacherResults({ active, tally, onDismiss }) {
  const q = active.question || {};
  return (
    <div className="fixed bottom-4 right-4 z-[70] w-[360px] max-w-[92vw] jasq-card p-3 border-2 border-[#3FA84D] bg-white shadow-2xl space-y-2" data-testid="teacher-results">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#2F7C3A]">
            Resultados · cerrada ({active.closed_reason})
          </p>
          <p className="text-sm font-bold text-[#1F2547] line-clamp-2"><RenderTextWithMath text={q.text || ""} /></p>
        </div>
        <button onClick={onDismiss} className="text-[#6E7591] text-xs underline shrink-0" data-testid="teacher-dismiss">Ocultar</button>
      </div>
      <TallyView question={q} tally={tally} highlightCorrect />
    </div>
  );
}

function StudentModal({ active, tally, alreadyAnswered, onSubmit, onDismiss, onMinimize }) {
  const q = active.question || {};
  const closed = active.status === "closed";
  return (
    <div className="fixed inset-0 z-[120] bg-[#1F2547]/70 backdrop-blur-sm grid place-items-center p-4" data-testid="student-quiz-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-4 py-2.5 bg-[#A43790] text-white flex items-center justify-between">
          <span className="text-xs font-extrabold inline-flex items-center gap-1.5">
            <Sparkles size={12} /> {closed ? "Resultados" : "Pregunta en vivo"}
          </span>
          <div className="flex items-center gap-2">
            {!closed && (
              <button onClick={onMinimize} className="text-white/90 hover:text-white text-[11px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/40" data-testid="student-minimize" title="Minimizar para volver a la clase">
                Minimizar
              </button>
            )}
            {closed && (
              <button onClick={onDismiss} className="text-white" data-testid="student-dismiss"><X size={14} /></button>
            )}
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {!closed && !alreadyAnswered && (
            <QuestionResponder question={q} onSubmit={onSubmit} />
          )}
          {!closed && alreadyAnswered && (
            <div className="text-center py-6 space-y-2">
              <CheckCircle2 size={48} className="text-[#3FA84D] mx-auto" />
              <p className="font-bold text-[#1F2547]">Respuesta enviada</p>
              <p className="text-xs text-[#6E7591]">Espera a que el docente cierre la pregunta para ver los resultados.</p>
              <p className="text-center text-sm font-bold text-[#1F2547] py-4">Gracias por tu respuesta.</p>
            </div>
          )}
          {closed && (
            <div className="space-y-3">
              <p className="text-sm font-bold text-[#1F2547]"><RenderTextWithMath text={q.text || ""} /></p>
              <p className="text-center text-sm font-bold text-[#1F2547] py-4">Gracias por tu respuesta.</p>
              <button
                onClick={onDismiss}
                data-testid="student-continue-class"
                className="w-full mt-1 px-3 py-2.5 rounded-xl bg-[#3FA84D] text-white text-sm font-extrabold hover:bg-[#2F7C3A] transition-colors"
              >
                Continuar con la clase
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TallyView({ question, tally, highlightCorrect = false, compact = false }) {
  const total = tally?.total || 0;
  const dist = tally?.distribution || {};
  const correctVal = useMemo(() => {
    const opt = (question.options || []).find((o) => o.is_correct);
    return opt ? String(opt.value ?? opt.label) : null;
  }, [question]);
  const maxBar = Math.max(1, ...Object.values(dist));
  return (
    <div className="space-y-1.5" data-testid="tally-view">
      <div className="flex items-center gap-2 text-[11px] font-bold text-[#1F2547]">
        <BarChart3 size={12} className="text-[#A43790]" />
        <span data-testid="tally-total">{total} respuesta(s)</span>
        {tally?.correct !== undefined && tally.correct > 0 && (
          <span className="text-[#3FA84D]">· {tally.correct} correcta(s)</span>
        )}
      </div>
      {question.type === "multiple_choice" && (question.options || []).map((o, i) => {
        const k = String(o.value ?? o.label);
        const count = dist[k] || 0;
        const pct = total ? Math.round((count / total) * 100) : 0;
        const isCorrect = highlightCorrect && correctVal === k;
        return (
          <div key={i} className="text-xs" data-testid={`tally-bar-${i}`}>
            <div className="flex justify-between items-center mb-0.5">
              <span className="font-bold text-[#1F2547] flex-1">
                <span className="inline-block w-4 h-4 rounded-full bg-[#1F2547] text-white text-[9px] font-extrabold text-center leading-4 mr-1">{String.fromCharCode(65 + i)}</span>
                <RenderTextWithMath text={o.label || ""} />
                {isCorrect && <span className="ml-1 text-[#3FA84D] font-extrabold">✓</span>}
              </span>
              <span className="text-[#6E7591] tabular-nums">{count} · {pct}%</span>
            </div>
            <div className="w-full h-1.5 bg-[#E1E5F0] rounded-full overflow-hidden">
              <div className={`h-full ${isCorrect ? "bg-[#3FA84D]" : "bg-[#A43790]"}`} style={{ width: `${(count / maxBar) * 100}%` }} />
            </div>
          </div>
        );
      })}
      {(question.type === "likert" || question.type === "scale_1_10") && (
        <div className="grid grid-cols-5 gap-1 text-[11px]">
          {Object.keys(dist).sort((a, b) => Number(a) - Number(b)).map((k) => (
            <div key={k} className="rounded bg-[#F7F8FA] p-1 text-center">
              <div className="font-extrabold text-[#A43790]">{dist[k]}</div>
              <div className="text-[#6E7591]">{k}</div>
            </div>
          ))}
        </div>
      )}
      {!compact && tally?.respondents?.length > 0 && (
        <div className="pt-1 mt-1 border-t border-[#E1E5F0]">
          <p className="text-[10px] uppercase tracking-wider text-[#6E7591] font-extrabold mb-0.5">Han respondido</p>
          <div className="flex flex-wrap gap-1">
            {tally.respondents.slice(0, 12).map((r, i) => {
              const showAns = !["multiple_choice", "likert", "scale_1_10"].includes(question.type) && r.answer != null && String(r.answer).trim() !== "";
              return (
                <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${r.is_correct === true ? "bg-[#F4FBEF] text-[#2F7C3A]" : r.is_correct === false ? "bg-[#FFF7DA] text-[#9C7A18]" : "bg-[#E8EEFB] text-[#2C49A0]"}`} data-testid={`tally-respondent-${i}`}>
                  {r.student_name}{showAns ? `: ${String(r.answer)}` : ""}{r.is_correct === true ? " ✓" : r.is_correct === false ? " ✗" : ""}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function LaunchModal({ classId, onClose, onLaunchBank, onLaunchInline, launching }) {
  const [tab, setTab] = useState("bank"); // bank | inline
  const [timeLimit, setTimeLimit] = useState(0); // 0 = sin tiempo
  return (
    <div className="fixed inset-0 z-[75] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4" data-testid="launch-modal" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[88vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#E1E5F0] flex items-center justify-between bg-white">
          <h3 className="font-display text-lg font-extrabold text-[#1F2547] inline-flex items-center gap-2">
            <Sparkles size={18} className="text-[#A43790]" /> Lanzar pregunta interactiva
          </h3>
          <button onClick={onClose} className="text-[#6E7591]" data-testid="launch-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-2 bg-[#F7F8FA] border-b border-[#E1E5F0] flex items-center gap-2 flex-wrap">
          <button onClick={() => setTab("bank")} data-testid="tab-launch-bank"
            className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${tab === "bank" ? "bg-[#A43790] text-white" : "bg-white text-[#1F2547] border-2 border-[#E1E5F0]"}`}>
            <Library size={11} /> Del banco
          </button>
          <button onClick={() => setTab("inline")} data-testid="tab-launch-inline"
            className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${tab === "inline" ? "bg-[#A43790] text-white" : "bg-white text-[#1F2547] border-2 border-[#E1E5F0]"}`}>
            <Wand2 size={11} /> Rápida (inline)
          </button>
          <div className="flex-1" />
          <label className="text-[11px] font-bold text-[#6E7591] inline-flex items-center gap-1.5">
            <Clock size={11} /> Tiempo límite:
            <select value={timeLimit} onChange={(e) => setTimeLimit(parseInt(e.target.value, 10))} className="text-xs px-2 py-1 rounded border-2 border-[#E1E5F0]" data-testid="launch-time-limit">
              <option value={0}>Sin límite</option>
              <option value={30}>30 segundos</option>
              <option value={60}>1 minuto</option>
              <option value={120}>2 minutos</option>
              <option value={300}>5 minutos</option>
            </select>
          </label>
        </div>
        <div className="flex-1 overflow-hidden">
          {tab === "bank" && (
            <BankPicker
              classId={classId}
              onPick={(it) => onLaunchBank(it)}
              launching={launching}
            />
          )}
          {tab === "inline" && (
            <InlineEditor onLaunch={(q) => onLaunchInline(q, timeLimit)} launching={launching} />
          )}
        </div>
      </div>
    </div>
  );
}

const DOMAIN_LABELS = {
  algebra: "Álgebra",
  aritmetica: "Aritmética",
  comprension_lectora: "Comprensión lectora",
  estadistica: "Estadística",
  geometria: "Geometría",
  razonamiento_logico: "Razonamiento lógico",
};

// v5.7.5 · búsqueda sin tildes ni mayúsculas
const normSearch = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
const flattenOptions = (o) => {
  if (!o) return "";
  if (typeof o === "string") return o;
  if (Array.isArray(o)) return o.map(flattenOptions).join(" ");
  if (typeof o === "object") return Object.values(o).map(flattenOptions).join(" ");
  return String(o);
};

function BankPicker({ classId, onPick, launching }) {
  // Carga /question-bank y permite filtrar por tipo (CEP/CAD) y área, además
  // de la búsqueda por texto.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");     // "" | "cep" | "cad"
  const [domain, setDomain] = useState(""); // "" | slug de área

  // v5.7.5 · se carga el banco UNA vez por tipo y la búsqueda se hace en el navegador: responde al instante,
  // ignora tildes y mayúsculas, y busca en el enunciado, las opciones, la plantilla, el bloque y el área.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (kind) params.kind = kind;
        const { data } = await api.get("/question-bank", { params });
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [kind]);

  // Áreas disponibles según lo cargado (depende del tipo elegido).
  const availableDomains = useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (it.domain) set.add(it.domain); });
    return Array.from(set).sort();
  }, [items]);

  const [limit, setLimit] = useState(60);
  useEffect(() => { setLimit(60); }, [q, kind, domain]);
  const searchIndex = useMemo(() => items.map((it) => ({
    it,
    hay: normSearch([
      it.text, it.template_code, it.template_title, it.block_title, it.type,
      DOMAIN_LABELS[it.domain] || it.domain, flattenOptions(it.options),
    ].filter(Boolean).join(" ")),
  })), [items]);
  const displayItems = useMemo(() => {
    const terms = normSearch(q).split(" ").filter(Boolean);
    return searchIndex
      .filter(({ it, hay }) => (!domain || it.domain === domain) && terms.every((t) => hay.includes(t)))
      .map(({ it }) => it);
  }, [searchIndex, q, domain]);

  return (
    <div className="flex flex-col h-[60vh]">
      <div className="px-5 py-2.5 border-b border-[#E1E5F0] space-y-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por texto, plantilla, área u opción…" autoFocus className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm" data-testid="launch-bank-search" />
        <div className="flex flex-wrap items-center gap-2">
          {/* Filtro CEP / CAD */}
          <div className="inline-flex items-center bg-[#F7F8FA] rounded-full p-0.5 border border-[#E1E5F0]" data-testid="bank-kind-filter">
            {[{ v: "", l: "Todos" }, { v: "cep", l: "CEP" }, { v: "cad", l: "CAD" }].map((opt) => (
              <button
                key={opt.v || "all"}
                type="button"
                onClick={() => { setKind(opt.v); setDomain(""); }}
                data-testid={`bank-kind-${opt.v || "all"}`}
                className={`text-[11px] font-extrabold px-3 py-1 rounded-full ${kind === opt.v ? "bg-[#A43790] text-white" : "text-[#4C5478]"}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {/* Filtro Área */}
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            data-testid="bank-domain-filter"
            className="text-xs px-2.5 py-1.5 rounded-full border-2 border-[#E1E5F0] font-bold text-[#1F2547] bg-white"
          >
            <option value="">Todas las áreas</option>
            {availableDomains.map((d) => (
              <option key={d} value={d}>{DOMAIN_LABELS[d] || d}</option>
            ))}
          </select>
          <span className="text-[11px] text-[#6E7591] font-bold ml-auto" data-testid="bank-result-count">{displayItems.length} pregunta(s)</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-2 space-y-1.5">
        {loading ? <p className="text-center text-sm text-[#6E7591] py-6">Cargando…</p>
          : displayItems.length === 0 ? <p className="text-center text-sm text-[#6E7591] py-6">Sin resultados</p>
          : displayItems.slice(0, limit).map((it) => (
            <button
              key={`${it.template_code}-${it.id}`}
              type="button"
              disabled={launching}
              onClick={() => onPick(it)}
              data-testid={`launch-bank-pick-${it.id}`}
              className="w-full text-left p-3 rounded-xl border-2 border-[#E1E5F0] hover:border-[#A43790] hover:bg-[#FFF1F8] disabled:opacity-40 transition-colors"
            >
              <div className="flex flex-wrap items-center gap-1 text-[10px] uppercase tracking-wider font-extrabold mb-0.5">
                <span className={`px-2 py-0.5 rounded-full text-white ${it.template_kind === "cep" ? "bg-[#A43790]" : "bg-[#2C49A0]"}`}>{(it.template_kind || "").toUpperCase() || it.type}</span>
                {it.domain && <span className="px-2 py-0.5 rounded-full bg-[#FFF7DA] text-[#9C7A18]">{DOMAIN_LABELS[it.domain] || it.domain}</span>}
                <span className="px-2 py-0.5 rounded-full bg-[#1F2547] text-white">{it.type}</span>
                <span className="text-[#6E7591]">· {it.template_code}</span>
              </div>
              <p className="text-sm font-bold text-[#1F2547] line-clamp-2"><RenderTextWithMath text={it.text || ""} /></p>
            </button>
          ))}
        {displayItems.length > limit && (
          <button type="button" onClick={() => setLimit((n) => n + 60)} data-testid="bank-show-more"
            className="w-full py-2 text-xs font-extrabold text-[#A43790] hover:underline">
            Mostrar más ({displayItems.length - limit} restantes)
          </button>
        )}
      </div>
    </div>
  );
}

function InlineEditor({ onLaunch, launching }) {
  const [text, setText] = useState("");
  const [opts, setOpts] = useState([
    { label: "Opción A", is_correct: true },
    { label: "Opción B", is_correct: false },
    { label: "Opción C", is_correct: false },
    { label: "Opción D", is_correct: false },
  ]);
  return (
    <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
      <div>
        <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] mb-1">Enunciado</p>
        <textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribe la pregunta… (puedes incluir [[math:LATEX]] para fórmulas)"
          data-testid="inline-text"
          className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">Opciones (marca la correcta)</p>
        {opts.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="radio"
              name="inline-correct"
              checked={o.is_correct}
              onChange={() => setOpts(opts.map((x, j) => ({ ...x, is_correct: j === i })))}
              data-testid={`inline-correct-${i}`}
            />
            <input
              value={o.label}
              onChange={(e) => {
                const next = [...opts]; next[i] = { ...next[i], label: e.target.value }; setOpts(next);
              }}
              data-testid={`inline-opt-${i}`}
              className="flex-1 px-2 py-1.5 rounded-lg border-2 border-[#E1E5F0] text-sm"
            />
            {opts.length > 2 && (
              <button onClick={() => setOpts(opts.filter((_, j) => j !== i))} className="text-[#A52258]"><X size={12} /></button>
            )}
          </div>
        ))}
        <button onClick={() => setOpts([...opts, { label: "Nueva opción", is_correct: false }])} className="text-[11px] font-bold text-[#A43790] hover:underline">
          + Agregar opción
        </button>
      </div>
      <button
        type="button"
        disabled={launching || !text.trim() || opts.length < 2}
        onClick={() => onLaunch({ type: "multiple_choice", text: text.trim(), options: opts.map((o, i) => ({ ...o, value: o.value ?? i })) })}
        data-testid="inline-launch"
        className="w-full jasq-btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-40"
      >
        <Play size={12} /> Lanzar pregunta
      </button>
    </div>
  );
}
